<template>
    <div>
        <b-card class="detail-card mb-2 raster-info-card" header-class="raster-info-card-header" :style="cardThemeStyle"
            header-text-variant="white">
            <template #header>
                <h4 class="font-weight-bold mb-1 text-center raster-name-text">
                    {{ nameText }}
                </h4>
                <h5 class="mb-0 text-center raster-address-text">
                    {{ addressWithCoordinates }}
                </h5>
            </template>
            <b-row class="no-gutters align-items-center raster-body-row">
                <b-col cols="5" class="text-center">
                    <h1 class="mb-0 font-weight-bold raster-last-value-text" v-if="lastValueText">
                        {{ lastValueText }}
                    </h1>
                </b-col>
                <b-col cols="2" class="text-center">
                    <b-img v-if="legendIconUrl" fluid class="raster-legend-icon" :src="legendIconUrl" />
                </b-col>
                <b-col cols="5" class="text-center">
                    <div class="raster-legend-text" v-if="lastLegendText">
                        {{ lastLegendText }}
                    </div>
                </b-col>
            </b-row>
            <div class="mt-1 text-center raster-update-text" v-if="lastUpdateText">
                Cập nhật lúc: {{ lastUpdateText }}
            </div>
        </b-card>
        <b-card class="detail-card mb-0" header-class="rainValueGather10-card-bg" header-text-variant="white">
            <div>
                <apex-chart :type="pointDetail.chartType" :height="320" :options="chartOptions" :series="seriesData" />
            </div>
        </b-card>
    </div>
</template>

<script>
import moment from 'moment-timezone';
import API_CONFIG from '@/api-config';

export default {
    props: {
        pointDetail: {
            type: Object,
            default: () => ({})
        }
    },
    computed: {
        nameText() {
            return this.pointDetail?.name;
        },
        addressWithCoordinates() {
            const address = this.pointDetail?.address || '';
            const lat = this.pointDetail?.lat;
            const lng = this.pointDetail?.lng;
            if (lat == null || lng == null) return address;
            return `${address} (${lat.toFixed(6)}, ${lng.toFixed(6)})`;
        },
        values() {
            if (!Array.isArray(this.pointDetail?.values)) return [];
            return this.pointDetail.values;
        },
        lastValueText() {
            if (this.values.length === 0) return '';
            const lastItem = this.values[this.values.length - 1];
            const value = lastItem?.value;
            if (value == null || Number.isNaN(value)) return '';
            return this.formatValue(value);
        },
        lastUpdateText() {
            if (this.values.length === 0) return '';
            const lastItem = this.values[this.values.length - 1];
            return this.formatFullTime(lastItem?.time);
        },
        lastValueNumber() {
            if (this.values.length === 0) return null;
            const lastItem = this.values[this.values.length - 1];
            const value = Number(lastItem?.value);
            if (Number.isNaN(value)) return null;
            return value;
        },
        legend() {
            if (!Array.isArray(this.pointDetail?.legend)) return [];
            return this.pointDetail.legend;
        },
        matchedLegendItem() {
            if (this.legend.length === 0 || this.lastValueNumber == null) return null;
            for (const item of this.legend) {
                if (this.lastValueNumber <= item.value) return item;
            }
            return this.legend[this.legend.length - 1] || null;
        },
        lastLegendText() {
            const item = this.matchedLegendItem;
            if (!item) return '';
            return item.name || '';
        },
        legendIconUrl() {
            const icon = this.matchedLegendItem?.icon;
            if (!icon) return '';
            if (icon.startsWith('http://') || icon.startsWith('https://')) return icon;
            return API_CONFIG.BASE_URL + 'download?fileName=' + icon;
        },
        themeColor() {
            return '#4a90d9';
        },
        headerThemeColor() {
            return this.darkenHexColor(this.themeColor, 24);
        },
        cardThemeStyle() {
            return {
                '--raster-card-bg': this.themeColor,
                '--raster-card-header-bg': this.headerThemeColor
            };
        },
        categories() {
            return this.values.map((item, index) => {
                const current = moment(item?.time);
                if (!current.isValid()) return '';
                if (index === 0) return current.format('DD/MM');

                const previous = moment(this.values[index - 1]?.time);
                if (!previous.isValid()) return current.format('HH:mm');
                if (!current.isSame(previous, 'day')) return current.format('DD/MM');
                return current.format('HH:mm');
            });
        },
        seriesData() {
            return [
                {
                    name: 'Giá trị',
                    data: this.values.map(item => item?.value ?? 0)
                }
            ];
        },
        chartOptions() {
            return {
                chart: {
                    id: 'raster-point-detail',
                    toolbar: {
                        tools: {
                            selection: false,
                            zoom: false,
                            zoomin: false,
                            zoomout: false,
                            reset: false,
                            pan: false
                        }
                    }
                },
                stroke: {
                    curve: 'smooth',
                    width: 2
                },
                dataLabels: {
                    enabled: false
                },
                grid: {
                    yaxis: {
                        lines: {
                            show: false
                        }
                    }
                },
                title: {
                    text: 'Lịch sử',
                    align: 'center',
                },
                xaxis: {
                    categories: this.categories,
                    tickAmount: Math.min(6, this.categories.length || 0),
                    labels: {
                        rotate: 0,
                        hideOverlappingLabels: true
                    }
                },
                yaxis: {
                    labels: {
                        formatter: value => this.formatValue(value)
                    }
                },
                tooltip: {
                    x: {
                        show: false
                    },
                    y: {
                        formatter: (value, { dataPointIndex }) =>
                            `${this.formatValue(value)}`
                    }
                }
            };
        }
    },
    methods: {
        formatTime(value) {
            if (!value) return '';
            return moment(value).format('HH:mm DD/MM');
        },
        formatFullTime(value) {
            if (!value) return '';
            return moment(value).format('DD/MM/YYYY HH:mm');
        },
        formatValue(value) {
            if (value == null || Number.isNaN(value)) return '';
            if (value === 0) return '0';
            if (Math.abs(value) < 0.1) return value.toFixed(2);
            if (Math.abs(value) < 1) return value.toFixed(1);
            return Number(value).toFixed(0);
        },
        darkenHexColor(color, amount) {
            const hex = (color || '').replace('#', '');
            if (![3, 6, 8].includes(hex.length)) return color || '#4d9bcf';
            const rawHex =
                hex.length === 8
                    ? hex.substring(0, 6)
                    : hex;
            const fullHex =
                rawHex.length === 3
                    ? rawHex
                        .split('')
                        .map(char => char + char)
                        .join('')
                    : rawHex;
            const r = Math.max(0, parseInt(fullHex.substring(0, 2), 16) - amount);
            const g = Math.max(0, parseInt(fullHex.substring(2, 4), 16) - amount);
            const b = Math.max(0, parseInt(fullHex.substring(4, 6), 16) - amount);
            const toHex = value => value.toString(16).padStart(2, '0');
            return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
        }
    }
};
</script>

<style scoped>
.raster-info-card {
    background-color: var(--raster-card-bg, #6fb8e4);
    color: #fff;
}

.raster-name-text {
    font-size: 1rem;
}

.raster-address-text {
    font-size: 0.875rem;
}

.raster-last-value-text {
    font-size: 3.4rem;
    line-height: 1.1;
}

.raster-body-row {
    min-height: 82px;
}

.raster-legend-icon {
    width: 68px;
    height: 68px;
    object-fit: contain;
}

.raster-legend-text {
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.2;
}

.raster-update-text {
    font-size: 0.8rem;
    opacity: 0.95;
}

::v-deep .raster-info-card .card-header {
    background-color: var(--raster-card-header-bg, #4d9bcf);
    color: #fff;
    border-bottom: 0;
    padding-bottom: 0.45rem;
}

::v-deep .raster-info-card .card-body {
    padding: 0.45rem 2rem 0.6rem;
}
</style>
